import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Form, Input, Select, Button, Tooltip } from "antd";
import {
  MoreOutlined,
  DeleteOutlined,
  PhoneOutlined,
  ArrowLeftOutlined,
  WhatsAppOutlined,
  VideoCameraOutlined,
  PlusOutlined,
  SmileOutlined,
  PaperClipOutlined,
  AudioOutlined,
  MessageOutlined,
  CheckOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { FetchPlaceholderFields } from "../../../assets/api/ScriptsService";
import { fetchUserFilters } from "../../../store/filters";
import { getTemplateComponents, AddTemplate } from "../../../assets/api/templateService";
import { useNavigate } from "react-router-dom";
import CustomNotification from "../../../Reuseable/CustomNotification/CustomNotification";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import { country as countryCodes } from "../../../assets/helpers/CountryCodes";
import "./CreateTemplate.scss";

const { TextArea } = Input;
const { Option } = Select;

const HEADER_LIMIT = 120;
const BODY_LIMIT = 1024;
const FOOTER_LIMIT = 120;

/* Shared dropdown skin used across the app (see Reuseable/Select/Select.scss).
   `wa-dropdown` only adds this page's spacing tweaks on top of it. */
const DROPDOWN_CLS = "custom-select-dropdown wa-dropdown";

// Static country code options mapped outside component scope to avoid recreation on every render
const countryCodeOptions = countryCodes.map((c) => (
  <Option key={`${c.iso}-${c.code}`} value={`${c.iso}_+${c.code}`}>
    {`${c.country} +${c.code}(${c.iso})`}
  </Option>
));

// Character usage meter rendered under each text area
const CharMeter = memo(({ value = 0, limit = 100 }) => {
  const percent = Math.min((value / limit) * 100, 100);
  const tone = percent >= 100 ? "is-full" : percent >= 80 ? "is-warn" : "";
  return (
    <div className={`wa-meter ${tone}`}>
      <span className="wa-meter__track">
        <span className="wa-meter__fill" style={{ width: `${percent}%` }} />
      </span>
      <span className="wa-meter__count">
        {value}/{limit}
      </span>
    </div>
  );
});
CharMeter.displayName = "CharMeter";

const CreateTemplate = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [bodyText, setBodyText] = useState("");
  const [isBodyExpanded, setIsBodyExpanded] = useState(false);
  const [footerText, setFooterText] = useState("");
  const [headerText, setHeaderText] = useState("");
  const [headerType, setHeaderType] = useState("text");
  const [submitting, setSubmitting] = useState(false);

  const token = useSelector((state) => state.auth.token);
  const dispatch = useDispatch();
  const { filter_list } = useSelector((state) => state.filters) || {};

  useEffect(() => {
    dispatch(fetchUserFilters());
  }, [dispatch]);

  const dynamicPortfoliosOptions = useMemo(() => {
    const portfoliosData = filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
    return portfoliosData.map((d) => ({
      value: d.id || d,
      label: d.name || d.key || d,
    }));
  }, [filter_list]);

  const [placeholderFields, setPlaceholderFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedHeaderPlaceholder, setSelectedHeaderPlaceholder] = useState(null);
  const [selectedBodyPlaceholder, setSelectedBodyPlaceholder] = useState(null);
  const [selectedFooterPlaceholder, setSelectedFooterPlaceholder] = useState(null);
  const [ctas, setCtas] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [maxButtons, setMaxButtons] = useState(3);

  const templateName = Form.useWatch("name", form);
  const templateLanguage = Form.useWatch("language", form);
  const templateCategory = Form.useWatch("category", form);

  useEffect(() => {
    if (token) {
      setLoadingComponents(true);
      getTemplateComponents(token)
        .then((res) => {
          if (res?.data?.data) {
            const { languages, categories, buttons } = res.data.data;
            if (languages) {
              setLanguageOptions(languages.map(lang => ({ label: lang.label, value: lang.code })));
            }
            if (categories) {
              setCategoryOptions(categories.map(cat => ({ label: cat.label, value: cat.value })));
            }
            if (buttons && buttons.max_count !== undefined) {
              setMaxButtons(buttons.max_count);
            }
          }
        })
        .catch((err) => console.error("Error fetching template components:", err))
        .finally(() => setLoadingComponents(false));
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      setLoadingFields(true);
      FetchPlaceholderFields(token)
        .then((res) => {
          let dataArray = [];
          if (res?.data?.data?.field_names && Array.isArray(res.data.data.field_names)) {
            dataArray = res.data.data.field_names;
          } else if (res?.data?.field_names && Array.isArray(res.data.field_names)) {
            dataArray = res.data.field_names;
          } else if (Array.isArray(res?.data)) {
            dataArray = res.data;
          } else if (Array.isArray(res?.data?.data)) {
            dataArray = res.data.data;
          }

          const formatLabel = (str) => {
            if (!str) return "";
            return str
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(" ");
          };

          const options = dataArray.map((item) => {
            const strVal = typeof item === "string" ? item : item?.name || item?.key || String(item);
            return { value: strVal, label: formatLabel(strVal) };
          });

          setPlaceholderFields(options);
        })
        .catch((err) => {
          console.error("Error fetching placeholders:", err);
        })
        .finally(() => {
          setLoadingFields(false);
        });
    }
  }, [token]);

  const handleHeaderPlaceholderSelect = useCallback((val) => {
    if (!val) return;
    const currentHeader = form.getFieldValue("header") || "";
    const placeholderText = `{{xbucketdata.${val}}}`;
    if (currentHeader.length + placeholderText.length > HEADER_LIMIT) {
      CustomNotification.show(`Placeholder cannot be added as it exceeds the ${HEADER_LIMIT} characters limit.`, "error");
      setSelectedHeaderPlaceholder(null);
      return;
    }
    const newHeader = currentHeader + placeholderText;
    form.setFieldsValue({ header: newHeader });
    setHeaderText(newHeader);
    setSelectedHeaderPlaceholder(null);
  }, [form]);

  const handleBodyPlaceholderSelect = useCallback((val) => {
    if (!val) return;
    const currentBody = form.getFieldValue("body") || "";
    const placeholderText = `{{xbucketdata.${val}}}`;
    if (currentBody.length + placeholderText.length > BODY_LIMIT) {
      CustomNotification.show(`Placeholder cannot be added as it exceeds the ${BODY_LIMIT} characters limit.`, "error");
      setSelectedBodyPlaceholder(null);
      return;
    }
    const newBody = currentBody + placeholderText;
    form.setFieldsValue({ body: newBody });
    setBodyText(newBody);
    setSelectedBodyPlaceholder(null);
  }, [form]);

  const handleFooterPlaceholderSelect = useCallback((val) => {
    if (!val) return;
    const currentFooter = form.getFieldValue("footer") || "";
    const placeholderText = `{{xbucketdata.${val}}}`;
    if (currentFooter.length + placeholderText.length > FOOTER_LIMIT) {
      CustomNotification.show(`Placeholder cannot be added as it exceeds the ${FOOTER_LIMIT} characters limit.`, "error");
      setSelectedFooterPlaceholder(null);
      return;
    }
    const newFooter = currentFooter + placeholderText;
    form.setFieldsValue({ footer: newFooter });
    setFooterText(newFooter);
    setSelectedFooterPlaceholder(null);
  }, [form]);

  const onFinish = useCallback(async (values) => {
    try {
      setSubmitting(true);

      const components = [];
      if (values.header && headerType === "text") {
        components.push({
          type: "HEADER",
          format: "TEXT",
          text: values.header,
        });
      }

      if (values.body) {
        components.push({
          type: "BODY",
          text: values.body,
        });
      }

      if (values.footer) {
        components.push({
          type: "FOOTER",
          text: values.footer,
        });
      }

      if (ctas && ctas.length > 0) {
        components.push({
          type: "BUTTONS",
          buttons: ctas.map((cta) => ({
            type: "PHONE_NUMBER",
            text: cta.label || "Phone",
            phone_number: `${cta.code ? (cta.code.includes('_') ? cta.code.split('_')[1] : cta.code) : ""}${cta.number || ""}`,
          })),
        });
      }

      const payload = {
        portfolio_id: Number(values.portfolio),
        channel: "whatsapp",
        name: values.name,
        language: values.language,
        category: values.category,
        components,
      };

      const res = await AddTemplate(token, payload);
      if (res.status === 201 || res.status === 200) {
        CustomNotification.show("Template created successfully", "success");
        navigate("/communication/whatsapp/template");
      }
    } catch (err) {
      CustomNotification.show(err || "Failed to create template", "error");
    } finally {
      setSubmitting(false);
    }
  }, [headerType, ctas, token, navigate]);

  const handleAddButton = useCallback((val) => {
    if (val === 'phone' && ctas.length < maxButtons) {
      setCtas(prevCtas => [...prevCtas, { id: Date.now(), label: "", code: "IN_+91", number: "" }]);
    }
  }, [ctas.length, maxButtons]);

  const handleCtaChange = useCallback((index, field, value) => {
    setCtas(prevCtas => {
      const newCtas = [...prevCtas];
      newCtas[index] = { ...newCtas[index], [field]: value };
      return newCtas;
    });
  }, []);

  const handleDeleteCta = useCallback((index) => {
    setCtas(prevCtas => prevCtas.filter((_, i) => i !== index));
  }, []);

  const languageLabel = useMemo(
    () => languageOptions.find((l) => l.value === templateLanguage)?.label,
    [languageOptions, templateLanguage]
  );
  const categoryLabel = useMemo(
    () => categoryOptions.find((c) => c.value === templateCategory)?.label,
    [categoryOptions, templateCategory]
  );

  const hasPreviewContent = Boolean(headerText || bodyText || footerText || ctas.length > 0);

  const previewTime = useMemo(
    () => new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
    []
  );

  // Reusable placeholder picker rendered beside each content label
  const renderPlaceholderPicker = (value, onChange) => (
    <Select
      showSearch
      className="wa-placeholder-select"
      popupClassName={DROPDOWN_CLS}
      placeholder="Insert placeholder"
      popupMatchSelectWidth={260}
      options={placeholderFields}
      loading={loadingFields}
      value={value}
      onChange={onChange}
      allowClear
      suffixIcon={<PlusOutlined />}
      filterOption={(input, option) =>
        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
      }
    />
  );

  return (
    <div className="create-template-container">
      <Form form={form} layout="vertical" onFinish={onFinish} className="wa-template-form">
        {/* Page header */}
        <div className="wa-topbar">
          <div className="wa-topbar__lead">
            <button
              type="button"
              className="wa-topbar__back"
              onClick={() => navigate("/communication/whatsapp/template")}
            >
              <ArrowLeftOutlined />
            </button>
            <span className="wa-topbar__badge">
              <WhatsAppOutlined />
            </span>
            <div className="wa-topbar__text">
              <h2>Create WhatsApp Template</h2>
              <p>Compose your message, add dynamic placeholders and preview it exactly as your customer will see it.</p>
            </div>
          </div>
          <div className="wa-topbar__actions">
            <NormalButton
              type="default"
              onClick={() => navigate("/communication/whatsapp/template")}
              className="wa-btn-ghost"
              width="104px"
            >
              Cancel
            </NormalButton>
            <NormalButton
              type="primary"
              htmlType="submit"
              loading={submitting}
              className="wa-btn-primary"
              width="150px"
            >
              Create Template
            </NormalButton>
          </div>
        </div>

        <div className="wa-layout">
          {/* Left column - form */}
          <div className="wa-layout__main">
            {/* Step 1 - details */}
            <section className="wa-card">
              <header className="wa-card__head">
                <span className="wa-card__step">1</span>
                <div className="wa-card__heading">
                  <h3>Template Details</h3>
                  <p>Basic information used to identify and route this template.</p>
                </div>
              </header>
              <div className="wa-card__body">
                <div className="wa-grid">
                  <Form.Item
                    label="Name"
                    name="name"
                    rules={[{ required: true, message: "Please enter template name" }]}
                  >
                    <Input placeholder="e.g. payment_reminder_v1" />
                  </Form.Item>
                  <Form.Item
                    label="Language"
                    name="language"
                    rules={[{ required: true, message: "Please select language" }]}
                  >
                    <Select
                      placeholder="Select Language"
                      popupClassName={DROPDOWN_CLS}
                      options={languageOptions}
                      loading={loadingComponents}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item
                    label="Portfolio"
                    name="portfolio"
                    rules={[{ required: true, message: "Please select portfolio" }]}
                  >
                    <Select
                      placeholder="Select Portfolio"
                      popupClassName={DROPDOWN_CLS}
                      options={dynamicPortfoliosOptions}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item
                    label="Category"
                    name="category"
                    rules={[{ required: true, message: "Please select category" }]}
                  >
                    <Select
                      placeholder="Select Category"
                      popupClassName={DROPDOWN_CLS}
                      options={categoryOptions}
                      loading={loadingComponents}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item
                    label="Template Type"
                    name="templateType"
                    initialValue="default"
                    rules={[{ required: true, message: "Please select template type" }]}
                  >
                    <Select placeholder="Default" popupClassName={DROPDOWN_CLS}>
                      <Option value="default">Default</Option>
                    </Select>
                  </Form.Item>
                </div>
              </div>
            </section>

            {/* Step 2 - content */}
            <section className="wa-card">
              <header className="wa-card__head">
                <span className="wa-card__step">2</span>
                <div className="wa-card__heading">
                  <h3>Message Content</h3>
                  <p>Header and footer are optional. Use placeholders to personalise each message.</p>
                </div>
              </header>
              <div className="wa-card__body">
                {/* Header */}
                <div className="wa-field">
                  <div className="wa-field__top">
                    <label className="wa-field__label">
                      Header <span className="wa-field__optional">Optional</span>
                    </label>
                    <div className="wa-field__tools">
                      {renderPlaceholderPicker(selectedHeaderPlaceholder, handleHeaderPlaceholderSelect)}
                      <Select
                        className="wa-type-select"
                        popupClassName={DROPDOWN_CLS}
                        value={headerType}
                        onChange={(value) => setHeaderType(value)}
                      >
                        <Option value="text">Text</Option>
                        <Option value="image" disabled>Image</Option>
                        <Option value="document" disabled>Document</Option>
                        <Option value="video" disabled>Video</Option>
                      </Select>
                    </div>
                  </div>
                  <Form.Item name="header" className="wa-field__control">
                    {headerType === "text" ? (
                      <TextArea
                        rows={2}
                        maxLength={HEADER_LIMIT}
                        onChange={(e) => setHeaderText(e.target.value)}
                        placeholder="Add a short title to the top of your message"
                      />
                    ) : (
                      <div className="wa-dropzone">
                        <CloudUploadOutlined />
                        <span>Click or drag a {headerType} file to upload</span>
                      </div>
                    )}
                  </Form.Item>
                  {headerType === "text" && <CharMeter value={headerText.length} limit={HEADER_LIMIT} />}
                </div>

                {/* Body */}
                <div className="wa-field">
                  <div className="wa-field__top">
                    <label className="wa-field__label is-required">Body</label>
                    <div className="wa-field__tools">
                      {renderPlaceholderPicker(selectedBodyPlaceholder, handleBodyPlaceholderSelect)}
                    </div>
                  </div>
                  <Form.Item
                    name="body"
                    className="wa-field__control"
                    rules={[{ required: true, message: "Please enter body text" }]}
                  >
                    <TextArea
                      rows={6}
                      maxLength={BODY_LIMIT}
                      onChange={(e) => setBodyText(e.target.value)}
                      placeholder="Enter the text for the template in the appropriate language"
                    />
                  </Form.Item>
                  <CharMeter value={bodyText.length} limit={BODY_LIMIT} />
                </div>

                {/* Footer */}
                <div className="wa-field">
                  <div className="wa-field__top">
                    <label className="wa-field__label">
                      Footer <span className="wa-field__optional">Optional</span>
                    </label>
                    <div className="wa-field__tools">
                      {renderPlaceholderPicker(selectedFooterPlaceholder, handleFooterPlaceholderSelect)}
                    </div>
                  </div>
                  <Form.Item name="footer" className="wa-field__control">
                    <TextArea
                      rows={2}
                      maxLength={FOOTER_LIMIT}
                      onChange={(e) => setFooterText(e.target.value)}
                      placeholder="Add a short line to the bottom of your message"
                    />
                  </Form.Item>
                  <CharMeter value={footerText.length} limit={FOOTER_LIMIT} />
                </div>
              </div>
            </section>

            {/* Step 3 - buttons */}
            <section className="wa-card">
              <header className="wa-card__head">
                <span className="wa-card__step">3</span>
                <div className="wa-card__heading">
                  <h3>Call To Action Buttons</h3>
                  <p>Add up to {maxButtons} buttons so customers can reach you in one tap.</p>
                </div>
                <span className="wa-card__counter">{ctas.length}/{maxButtons}</span>
              </header>
              <div className="wa-card__body">
                <Select
                  value={null}
                  placeholder="Add Button"
                  className="wa-add-button"
                  popupClassName={DROPDOWN_CLS}
                  disabled={ctas.length >= maxButtons}
                  onChange={handleAddButton}
                  suffixIcon={<PlusOutlined />}
                >
                  <Option value="phone">Phone Number</Option>
                </Select>
                {ctas.length >= maxButtons && (
                  <p className="wa-hint">Maximum {maxButtons} buttons allowed.</p>
                )}

                {ctas.length === 0 ? (
                  <div className="wa-empty-ctas">
                    <PhoneOutlined />
                    <p>No buttons added yet. Buttons are optional but boost response rates.</p>
                  </div>
                ) : (
                  <div className="wa-cta-list">
                    {ctas.map((cta, index) => (
                      <div key={cta.id} className="wa-cta">
                        <div className="wa-cta__head">
                          <span className="wa-cta__index">Call To Action {index + 1}</span>
                          <Tooltip title="Remove button">
                            <Button
                              type="text"
                              className="wa-cta__remove"
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteCta(index)}
                            />
                          </Tooltip>
                        </div>
                        <div className="wa-cta__row">
                          <div className="wa-cta__cell">
                            <span className="wa-cta__label">Button Text</span>
                            <Input
                              placeholder="Phone"
                              value={cta.label}
                              onChange={(e) => handleCtaChange(index, "label", e.target.value)}
                            />
                          </div>
                          <div className="wa-cta__cell">
                            <span className="wa-cta__label">Type</span>
                            <Input value="PHONE_NUMBER" disabled />
                          </div>
                          <div className="wa-cta__cell">
                            <span className="wa-cta__label">Country Code</span>
                            <Select
                              showSearch
                              popupClassName={`${DROPDOWN_CLS} is-wide`}
                              popupMatchSelectWidth={280}
                              value={cta.code}
                              onChange={(val) => handleCtaChange(index, "code", val)}
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                            >
                              {countryCodeOptions}
                            </Select>
                          </div>
                          <div className="wa-cta__cell">
                            <span className="wa-cta__label">Phone Number</span>
                            <Input
                              placeholder="1234567890"
                              maxLength={10}
                              value={cta.number}
                              onChange={(e) => {
                                const numericValue = e.target.value.replace(/\D/g, "");
                                handleCtaChange(index, "number", numericValue);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Bottom actions (visible on narrow screens) */}
            <div className="wa-bottom-actions">
              <NormalButton
                type="default"
                onClick={() => navigate("/communication/whatsapp/template")}
                className="wa-btn-ghost"
                width="104px"
              >
                Cancel
              </NormalButton>
              <NormalButton
                type="primary"
                htmlType="submit"
                loading={submitting}
                className="wa-btn-primary"
                width="150px"
              >
                Create Template
              </NormalButton>
            </div>
          </div>

          {/* Right column - live preview */}
          <aside className="wa-layout__aside">
            <div className="wa-preview">
              <div className="wa-preview__head">
                <div>
                  <span className="wa-preview__eyebrow">Live Preview</span>
                  <h4>{templateName || "Untitled template"}</h4>
                </div>
                <span className="wa-preview__dot" />
              </div>

              <div className="wa-preview__tags">
                {categoryLabel && <span className="wa-tag">{categoryLabel}</span>}
                {languageLabel && <span className="wa-tag is-muted">{languageLabel}</span>}
                {!categoryLabel && !languageLabel && (
                  <span className="wa-tag is-muted">Select a category &amp; language</span>
                )}
              </div>

              <div className="wa-phone">
                <span className="wa-phone__notch" />
                <div className="wa-phone__screen">
                  <div className="wa-chat__topbar">
                    <ArrowLeftOutlined className="wa-chat__back" />
                    <div className="wa-chat__avatar">
                      <WhatsAppOutlined />
                    </div>
                    <div className="wa-chat__identity">
                      <span className="wa-chat__name">Business Account</span>
                      <span className="wa-chat__status">online</span>
                    </div>
                    <VideoCameraOutlined className="wa-chat__icon" />
                    <PhoneOutlined className="wa-chat__icon" />
                    <MoreOutlined className="wa-chat__icon" />
                  </div>

                  <div className="wa-chat__body">
                    <span className="wa-chat__date">
                      {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>

                    {hasPreviewContent ? (
                      <div className="wa-bubble">
                        <div className="wa-bubble__content">
                          {headerText && headerType === "text" && (
                            <div className="wa-bubble__header">{headerText}</div>
                          )}
                          {bodyText && (
                            <div className="wa-bubble__body">
                              {bodyText.length > 150 ? (
                                <>
                                  {isBodyExpanded ? bodyText : `${bodyText.substring(0, 150)}... `}
                                  <span
                                    className="wa-bubble__toggle"
                                    onClick={() => setIsBodyExpanded(!isBodyExpanded)}
                                  >
                                    {isBodyExpanded ? " show less" : " show more"}
                                  </span>
                                </>
                              ) : (
                                bodyText
                              )}
                            </div>
                          )}
                          {footerText && <div className="wa-bubble__footer">{footerText}</div>}
                          <span className="wa-bubble__time">
                            {previewTime} <CheckOutlined />
                          </span>
                        </div>

                        {ctas.length > 0 && (
                          <div className="wa-bubble__actions">
                            {ctas.map((cta) => (
                              <div key={cta.id} className="wa-bubble__action">
                                <PhoneOutlined />
                                {cta.label || "Phone"}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="wa-chat__empty">
                        <MessageOutlined />
                        <p>Start typing your message to see the preview here.</p>
                      </div>
                    )}
                  </div>

                  <div className="wa-chat__inputbar">
                    <SmileOutlined />
                    <span className="wa-chat__input">Message</span>
                    <PaperClipOutlined />
                    <span className="wa-chat__mic">
                      <AudioOutlined />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </Form>
    </div>
  );
};

export default memo(CreateTemplate);
